import axios from '../../utils/axiosConfig';

export const CREATE_CHECKOUT_SESSION_REQUEST = 'CREATE_CHECKOUT_SESSION_REQUEST';
export const CREATE_CHECKOUT_SESSION_SUCCESS = 'CREATE_CHECKOUT_SESSION_SUCCESS';
export const CREATE_CHECKOUT_SESSION_FAIL = 'CREATE_CHECKOUT_SESSION_FAIL';

export const SET_CHECKOUT_MANIFEST = 'SET_CHECKOUT_MANIFEST';
export const CLEAR_CHECKOUT_MANIFEST = 'CLEAR_CHECKOUT_MANIFEST';

// NEW
export const CONFIRM_PAYMENT_REQUEST = 'CONFIRM_PAYMENT_REQUEST';
export const CONFIRM_PAYMENT_SUCCESS = 'CONFIRM_PAYMENT_SUCCESS';
export const CONFIRM_PAYMENT_FAIL = 'CONFIRM_PAYMENT_FAIL';

export const FETCH_PAYMENTS_REQUEST = 'FETCH_PAYMENTS_REQUEST';
export const FETCH_PAYMENTS_SUCCESS = 'FETCH_PAYMENTS_SUCCESS';
export const FETCH_PAYMENTS_FAIL = 'FETCH_PAYMENTS_FAIL';

export const FETCH_PAYMENT_DETAILS_REQUEST = 'FETCH_PAYMENT_DETAILS_REQUEST';
export const FETCH_PAYMENT_DETAILS_SUCCESS = 'FETCH_PAYMENT_DETAILS_SUCCESS';
export const FETCH_PAYMENT_DETAILS_FAIL = 'FETCH_PAYMENT_DETAILS_FAIL';

// EasyPay's Checkout SDK expects the create-session manifest:
// { id, session, config }. GET/status responses can also contain an id, but
// they are not startable manifests and the SDK rejects them.
export const isStartableCheckoutManifest = (m) =>
  !!m &&
  typeof m === 'object' &&
  typeof m.id === 'string' &&
  m.id.length > 0 &&
  typeof m.session === 'string' &&
  m.session.length > 0;

const isPendingPaymentManifest = (m) => {
  if (!m || typeof m !== 'object') return false;

  const method = m?.payment?.method || m?.method || m?.transaction?.method;
  const methodType =
    (typeof method === 'string' ? method : method?.type || method?.method_type) ||
    m?.payment?.paymentMethod ||
    m?.paymentMethod ||
    m?.activePayment?.paymentMethod;
  const entity =
    method?.entity ||
    m?.payment?.entity ||
    m?.entity ||
    m?.mbEntity ||
    m?.activePayment?.mbEntity;
  const reference =
    method?.reference ||
    m?.payment?.reference ||
    m?.reference ||
    m?.mbReference ||
    m?.activePayment?.mbReference;

  return (
    m._pendingPayment === true ||
    methodType === 'mb' ||
    !!(entity && reference)
  );
};

export const isUsableCheckoutManifest = (m) => {
  if (!m || typeof m !== 'object') return false;
  if (typeof m.id !== 'string' || m.id.length === 0) return false;
  return isStartableCheckoutManifest(m) || isPendingPaymentManifest(m);
};

export const getMultibancoMethod = (manifest) => {
  if (!manifest || typeof manifest !== 'object') return null;

  const method =
    manifest?.payment?.method ||
    manifest?.method ||
    manifest?.transaction?.method ||
    null;
  const methodType =
    (typeof method === 'string' ? method : method?.type || method?.method_type) ||
    manifest?.payment?.paymentMethod ||
    manifest?.paymentMethod ||
    manifest?.activePayment?.paymentMethod;
  const entity =
    method?.entity ||
    manifest?.payment?.entity ||
    manifest?.entity ||
    manifest?.mbEntity ||
    manifest?.activePayment?.mbEntity;
  const reference =
    method?.reference ||
    manifest?.payment?.reference ||
    manifest?.reference ||
    manifest?.mbReference ||
    manifest?.activePayment?.mbReference;
  const value =
    method?.value ??
    method?.amount ??
    manifest?.payment?.value ??
    manifest?.payment?.amount ??
    manifest?.value ??
    manifest?.amount ??
    manifest?.mbAmount ??
    manifest?.activePayment?.mbAmount;
  const expirationDate =
    method?.expiration_date ||
    method?.expires_at ||
    method?.expirationDate ||
    manifest?.payment?.expiration_date ||
    manifest?.payment?.expires_at ||
    manifest?.payment?.expirationDate ||
    manifest?.expiration_date ||
    manifest?.expires_at ||
    manifest?.expirationDate ||
    manifest?.mbExpiresAt ||
    manifest?.activePayment?.mbExpiresAt;

  if (methodType !== 'mb' && !entity && !reference) return null;

  return {
    type: 'mb',
    checkoutId:
      manifest?.checkoutId || manifest?.id || manifest?.activePayment?.checkoutId,
    entity,
    reference,
    value,
    expiration_date: expirationDate,
    pendingOnly: !entity && !reference,
  };
};

export const isMultibancoActive = (manifest) => {
  const mb = getMultibancoMethod(manifest);
  if (!mb || !mb.expiration_date) return !!mb;
  const expiresMs = Date.parse(mb.expiration_date);
  if (Number.isNaN(expiresMs)) return true;
  return expiresMs > Date.now();
};

// --- checkout ---
export const createCheckoutSession = (checkoutData) => async (dispatch, getState) => {
  const { auth } = getState();
  const token = auth.token;

  dispatch({ type: CREATE_CHECKOUT_SESSION_REQUEST });

  try {
    const res = await axios.post('/payments/checkout', checkoutData, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!isUsableCheckoutManifest(res.data)) {
      const err = new Error('Invalid checkout manifest returned by server');
      err.code = 'INVALID_MANIFEST';
      err.response = { data: res.data };
      throw err;
    }

    dispatch({ type: CREATE_CHECKOUT_SESSION_SUCCESS, payload: res.data });
    dispatch(setCheckoutManifest(res.data));
    return res.data;
  } catch (error) {
    dispatch({
      type: CREATE_CHECKOUT_SESSION_FAIL,
      payload: error.response?.data?.message || 'Failed to create checkout session',
    });
    throw error;
  }
};

export const setCheckoutManifest = (manifest) => ({
  type: SET_CHECKOUT_MANIFEST,
  payload: manifest,
});

export const clearCheckoutManifest = () => ({ type: CLEAR_CHECKOUT_MANIFEST });

// --- confirm (server verifies via Easypay) ---
export const confirmPayment =
  ({ bookingId, checkoutId, checkoutInfo } = {}) =>
  async (dispatch, getState) => {
    const { auth } = getState();
    const token = auth.token;

    dispatch({ type: CONFIRM_PAYMENT_REQUEST });

    try {
      const res = await axios.post(
        '/payments/confirm',
        { bookingId, checkoutId, checkoutInfo },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      dispatch({ type: CONFIRM_PAYMENT_SUCCESS, payload: res.data });
      return res.data;
    } catch (error) {
      dispatch({
        type: CONFIRM_PAYMENT_FAIL,
        payload: error.response?.data?.message || 'Failed to confirm payment',
      });
      throw error;
    }
  };

// --- history list ---
export const fetchPayments = (params = {}) => async (dispatch, getState) => {
  const { auth } = getState();
  const token = auth.token;

  dispatch({ type: FETCH_PAYMENTS_REQUEST });

  try {
    const res = await axios.get('/payments', {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });

    dispatch({ type: FETCH_PAYMENTS_SUCCESS, payload: res.data });
    return res.data;
  } catch (error) {
    dispatch({
      type: FETCH_PAYMENTS_FAIL,
      payload: error.response?.data?.message || 'Failed to fetch payments',
    });
    throw error;
  }
};

// --- payment detail ---
export const fetchPaymentDetails = (paymentId) => async (dispatch, getState) => {
  const { auth } = getState();
  const token = auth.token;

  dispatch({ type: FETCH_PAYMENT_DETAILS_REQUEST });

  try {
    const res = await axios.get(`/payments/${paymentId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    dispatch({ type: FETCH_PAYMENT_DETAILS_SUCCESS, payload: res.data });
    return res.data;
  } catch (error) {
    dispatch({
      type: FETCH_PAYMENT_DETAILS_FAIL,
      payload: error.response?.data?.message || 'Failed to fetch payment details',
    });
    throw error;
  }
};
